import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, BellOff, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

// Push is only meaningful on the installed/standalone PWA over HTTPS with a
// registered service worker. Never request permission on page load.
export default function PushOptIn() {
  const { token, user } = useAuth();
  const [state, setState] = useState("idle"); // idle | working | enabled | denied | unsupported
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok || !token) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => { if (sub) setState("enabled"); })
      .catch(() => {});
  }, [token]);

  if (!user || !supported) return null;

  const enable = async () => {
    setState("working");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState("denied");
        toast.error("Notifications are blocked — enable them in your browser settings.");
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/public-key`);
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      await axios.post(`${API}/push/subscriptions`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setState("enabled");
      toast.success("Order & shipping notifications are on for this device.");
    } catch (e) {
      console.error(e);
      setState("idle");
      toast.error("Couldn't enable notifications on this device.");
    }
  };

  const disable = async () => {
    setState("working");
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await axios.delete(`${API}/push/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { endpoint: subscription.endpoint },
        });
        await subscription.unsubscribe();
      }
      setState("idle");
      toast.success("Notifications turned off for this device.");
    } catch {
      setState("enabled");
      toast.error("Couldn't update notification settings.");
    }
  };

  return (
    <div className="border border-[#232B40] bg-surface p-6 sm:p-8 space-y-4" data-testid="push-optin-card">
      <p className="text-[10px] uppercase tracking-[0.25em] text-clinical flex items-center gap-2">
        <Bell className="h-4 w-4" strokeWidth={1.5} /> Notifications
      </p>
      <p className="text-xs text-[#8E96AB] leading-relaxed">
        Get order confirmations, shipping updates, and receipts as push notifications on this device — even when the app is closed. Delivered securely; you can turn it off anytime.
      </p>
      {state === "enabled" ? (
        <button
          onClick={disable}
          data-testid="push-toggle-button"
          className="inline-flex items-center gap-2 border border-clinical/40 text-clinical text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 hover:bg-clinical hover:text-[#0B101C] transition-colors duration-300"
        >
          <Check className="h-3.5 w-3.5" /> On — turn off
        </button>
      ) : (
        <button
          onClick={enable}
          disabled={state === "working"}
          data-testid="push-toggle-button"
          className="inline-flex items-center gap-2 bg-clinical text-[#0B101C] text-[10px] uppercase tracking-[0.2em] font-semibold px-5 py-2.5 hover:bg-[#DBC28C] transition-colors duration-300 disabled:opacity-50"
        >
          {state === "working" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
          {state === "denied" ? "Blocked — enable in browser settings" : "Enable notifications"}
        </button>
      )}
    </div>
  );
}
